import * as log from '@sb/log';
import {
  PrimitiveEventType,
  TCState,
  type ControllerStateMsg,
  type Primitive,
  type PrimitiveEventMsg,
} from '@sb/messenger/auto_generated/messenger/trajectory_controller_service';
import type { TrajectoryControllerConnector } from '@sb/messenger/connectors/generated';

import {
  enqueueWithTcPrerequisites,
  type TcPrerequisiteSource,
} from '../../enqueueWithTcPrerequisites';

import {
  DEFAULT_SERVO_CONFIG,
  type ExternalControlServoConfig,
} from './Arguments';

const ns = log.namespace('TcServoSession');

// The TC's control-loop rate, used to convert the customer-facing smoothing
// time constant (tauMs) into the TC's per-tick low-pass gain:
// k_p = 1 − exp(−dt/τ). Must match [control].loop_hz in tc-tuning.toml; if
// the loop rate ever becomes variable, this transform belongs in the TC
// itself (which knows its own dt).
const TC_CONTROL_LOOP_HZ = 1000;

/** Convert the customer-facing τ (ms) to the TC wire gain. */
function tauMsToKp(tauMs: number): number {
  const dtMs = 1000 / TC_CONTROL_LOOP_HZ;

  return 1 - Math.exp(-dtMs / tauMs);
}

const ENQUEUE_TIMEOUT_MS = 3000;
const SET_SAFETY_SESSION_TIMEOUT_MS = 3000;
const STOP_RPC_TIMEOUT_MS = 1000;
const CLEAR_ERROR_TIMEOUT_MS = 1000;
const CLEAR_QUEUE_TIMEOUT_MS = 1000;
const DEFAULT_IDLE_WAIT_TIMEOUT_MS = 2000;

/**
 * Optional ServoJ tuning for the enqueue. Any omitted field falls back to
 * `DEFAULT_SERVO_CONFIG`. Sourced from the customer `start()` request.
 */
export type ServoConfig = ExternalControlServoConfig;

export interface TcServoSessionOptions {
  /**
   * Called once when the session ends without a stop() — a `PRIMITIVE_FAILED`
   * event, a `TC_ERROR` controller state, or an unexpected
   * `PRIMITIVE_COMPLETE` (the ServoJ command timeout reports as a successful
   * completion). The owning step fails the routine and releases the lease;
   * the client re-establishes on restart.
   */
  onFault?: (reason: string) => void;
  /** Max time to wait for the TC to reach IDLE during stop() before proceeding. */
  idleWaitTimeoutMs?: number;
  /** Per-session ServoJ tuning; omitted fields fall back to the defaults. */
  servoConfig?: ServoConfig;
}

/**
 * Drives the trajectory-controller ServoJ control-plane for a TC-routed external
 * streaming session. The high-rate `WriteServoJ` target stream is pushed by
 * external-control-bot; this owns only the lifecycle RPCs:
 *
 *  - start(): `ClearError` (defensive; a prior session may have left ERROR) →
 *    `Enqueue(ServoJ)` → subscribe `ControllerState`/`PrimitiveEvent`.
 *  - stop():  `Stop` → wait for `TC_IDLE` → `ClearQueue` → `ClearError` if the
 *    TC was left in ERROR → unsubscribe.
 *
 * The lease itself is owned by the External Control step, acquired before start()
 * and released only after stop() reaches IDLE — releasing mid-STOPPING forces a
 * spurious TC error. Faults are reported via `onFault`; this never auto-recovers.
 */
export class TcServoSession {
  private primitiveId: number | undefined;

  private subscribed = false;

  private latestState: TCState = TCState.TC_IDLE;

  private faulted = false;

  /** Set by stop(); checked after every await in start() so a stop landing
   * mid-start never leaves a live primitive or subscription behind. */
  private stopRequested = false;

  private startPromise: Promise<void> | null = null;

  /** True once start() settled (either way); lets stop() skip the wait-out. */
  private startSettled = false;

  private stopPromise: Promise<void> | null = null;

  private readonly idleWaiters: Array<() => void> = [];

  private readonly idleWaitTimeoutMs: number;

  private readonly onFault: (reason: string) => void;

  private readonly servoConfig: ServoConfig;

  constructor(
    private readonly connector: TrajectoryControllerConnector,
    private readonly prereqSource: TcPrerequisiteSource,
    options: TcServoSessionOptions = {},
  ) {
    this.onFault = options.onFault ?? (() => {});

    this.idleWaitTimeoutMs =
      options.idleWaitTimeoutMs ?? DEFAULT_IDLE_WAIT_TIMEOUT_MS;

    this.servoConfig = options.servoConfig ?? {};
  }

  /**
   * Enqueue the ServoJ primitive and begin observing the TC. Must run after the
   * `TRAJECTORY_CONTROLLER` lease is acquired so the primitive seeds its filter
   * to the real current position.
   */
  public start(): Promise<void> {
    this.startPromise ??= this.doStart().finally(() => {
      this.startSettled = true;
    });

    return this.startPromise;
  }

  private async doStart(): Promise<void> {
    // ClearError before Enqueue on (re)start — a prior session may have left
    // the TC in ERROR, and the TC rejects an Enqueue from ERROR.
    // Idempotent: a no-op when already IDLE.
    await this.connector.ClearError({}, CLEAR_ERROR_TIMEOUT_MS);

    if (this.stopRequested) {
      return;
    }

    // The enqueue is the servo "start()" and carries settings only — joint
    // targets arrive exclusively via the WriteServoJ sink. The primitive holds
    // the current commanded position until the first streamed target lands.
    const primitive: Primitive = {
      servoJ: {
        // Customer vocabulary → TC vocabulary: τ in ms becomes the
        // per-tick gain the primitive consumes.
        kP: tauMsToKp(this.servoConfig.tauMs ?? DEFAULT_SERVO_CONFIG.tauMs),
        commandTimeoutMs:
          this.servoConfig.commandTimeoutMs ??
          DEFAULT_SERVO_CONFIG.commandTimeoutMs,
        rampInDurationMs:
          this.servoConfig.rampInDurationMs ??
          DEFAULT_SERVO_CONFIG.rampInDurationMs,
      },
    };

    const reply = await enqueueWithTcPrerequisites(
      this.connector,
      primitive,
      this.prereqSource,
      {
        enqueueTimeoutMs: ENQUEUE_TIMEOUT_MS,
        setSafetySessionTimeoutMs: SET_SAFETY_SESSION_TIMEOUT_MS,
        isCancelled: () => this.stopRequested,
      },
    );

    if (this.stopRequested) {
      // A stop landed while the enqueue was in flight. stop() is awaiting this
      // promise and tears down whatever was enqueued — don't subscribe or treat
      // the session as live.
      log.warn(ns`start`, 'Stopped during ServoJ enqueue; skipping subscribe');
      this.primitiveId = reply.accepted ? reply.primitiveId : undefined;

      return;
    }

    if (!reply.accepted) {
      throw new Error(`TC rejected ServoJ enqueue: error=${reply.error}`);
    }

    this.primitiveId = reply.primitiveId;

    // Subscribe AFTER Enqueue — the RPC triggers service discovery so the topic
    // ports are cached and the SUB sockets connect immediately.
    this.connector.subscribeToControllerState(
      this.onControllerState.bind(this),
    );

    this.connector.subscribeToPrimitiveEvent(this.onPrimitiveEvent.bind(this));
    this.subscribed = true;

    log.info(ns`start`, 'ServoJ session enqueued', {
      primitiveId: this.primitiveId,
    });
  }

  /**
   * Normal teardown: decelerate to IDLE, drop any pending queue entries, and
   * clear any ERROR the stop itself induced (a Stop landing on an unauthorized
   * TC parks it in ERROR instead of decelerating). The caller must not release
   * the lease until this resolves — releasing mid-STOPPING forces a spurious
   * TC error.
   *
   * Idempotent: concurrent callers share one teardown. A stop landing while
   * start() is still in flight waits the start out first, so a just-enqueued
   * primitive is stopped here instead of orphaned.
   */
  public stop(): Promise<void> {
    this.stopRequested = true;
    this.stopPromise ??= this.doStop();

    return this.stopPromise;
  }

  private async doStop(): Promise<void> {
    // Wait out an in-flight start() before tearing down, so a just-enqueued
    // primitive is stopped here rather than orphaned; its own failure (if any)
    // surfaces to start()'s caller, not here. Skipped when start already
    // settled so the Stop RPC goes out immediately.
    if (!this.startSettled && this.startPromise) {
      await this.startPromise.catch(() => {});
    }

    try {
      const reply = await this.connector.Stop({}, STOP_RPC_TIMEOUT_MS);
      this.latestState = reply.state;

      if (this.latestState !== TCState.TC_IDLE) {
        await this.waitForIdle();
      }
    } catch (err) {
      log.warn(ns`stop`, 'ServoJ session Stop RPC failed', { err });
    }

    // Best effort even when the Stop RPC failed — dropping pending queue
    // entries is independent of the decel, and skipping it would leave the
    // queue populated after the lease is released.
    try {
      const reply = await this.connector.ClearQueue({}, CLEAR_QUEUE_TIMEOUT_MS);

      this.latestState = reply.state;
    } catch (err) {
      log.warn(ns`stop`, 'ServoJ session ClearQueue failed', { err });
    }

    // Leave the TC as we found it: IDLE. A Stop issued while the TC is
    // unauthorized (the arm safety-stopped mid-session) parks it in ERROR
    // rather than decelerating to IDLE, and nothing outside this session
    // clears that state unless the routine ends in Failure and is recovered —
    // TC jogging has no ClearError of its own, so an ERROR left behind here
    // bricks jogging until the next streaming session starts. Safe at this
    // point: the arm is already stopped and any fault reason was delivered
    // via onFault before teardown began.
    if (this.latestState === TCState.TC_ERROR) {
      try {
        await this.connector.ClearError({}, CLEAR_ERROR_TIMEOUT_MS);
      } catch (err) {
        log.warn(ns`stop`, 'ServoJ session teardown ClearError failed', {
          err,
        });
      }
    }

    this.unsubscribe();
  }

  private waitForIdle(): Promise<void> {
    return new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        log.warn(ns`waitForIdle`, 'Timed out waiting for TC_IDLE; proceeding');
        resolve();
      }, this.idleWaitTimeoutMs);

      this.idleWaiters.push(() => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }

  private onControllerState(msg: ControllerStateMsg): void {
    this.latestState = msg.state;

    // IDLE (target reached) or ERROR (can't reach IDLE) both release stop()'s
    // wait so it doesn't hang.
    if (msg.state === TCState.TC_IDLE || msg.state === TCState.TC_ERROR) {
      const waiters = this.idleWaiters.splice(0);
      for (const resolve of waiters) resolve();
    }

    if (msg.state === TCState.TC_ERROR) {
      this.fault(`controller state TC_ERROR errors=${msg.errors}`);
    }
  }

  private onPrimitiveEvent(event: PrimitiveEventMsg): void {
    // The event topic is a global bus — filter by our primitive id.
    if (
      event.primitiveId !== undefined &&
      this.primitiveId !== undefined &&
      event.primitiveId !== this.primitiveId
    ) {
      return;
    }

    if (event.type === PrimitiveEventType.PRIMITIVE_FAILED) {
      this.fault(`ServoJ primitive failed: error=${event.error}`);

      return;
    }

    // ServoJ never completes on its own except through the command timeout —
    // a session stop decelerates through STOPPING and emits PRIMITIVE_STOPPED
    // instead. The TC reports the timeout as a *successful* completion, so
    // without this the session dies silently: the routine keeps running with
    // the lease held while every subsequent streamed target lands in a
    // mailbox no primitive reads. Skip once our own stop is in flight — a
    // timeout racing the stop is not a fault, the session is ending anyway.
    if (
      event.type === PrimitiveEventType.PRIMITIVE_COMPLETE &&
      !this.stopRequested
    ) {
      const timeoutMs =
        this.servoConfig.commandTimeoutMs ??
        DEFAULT_SERVO_CONFIG.commandTimeoutMs;

      this.fault(`Joint streaming timeout (${timeoutMs} ms) exceeded.`);
    }
  }

  private fault(reason: string): void {
    if (this.faulted) {
      return;
    }

    this.faulted = true;
    log.error(ns`fault`, 'ServoJ session faulted', { reason });
    this.onFault(reason);
  }

  private unsubscribe(): void {
    if (!this.subscribed) {
      return;
    }

    this.subscribed = false;

    try {
      this.connector.unsubscribeFromControllerState();
    } catch (err) {
      log.warn(ns`unsubscribe`, 'Failed to unsubscribe from ControllerState', {
        err,
      });
    }

    try {
      this.connector.unsubscribeFromPrimitiveEvent();
    } catch (err) {
      log.warn(ns`unsubscribe`, 'Failed to unsubscribe from PrimitiveEvent', {
        err,
      });
    }
  }
}
